import {
    FeatureFlag,
    FeatureFlagsExistenceLogicalOperator,
} from "@common-shared/config/featureFlags.config"
import {
    OneVerifyPermission,
    PermissionsExistenceLogicalOperator,
} from "@common-shared/config/rolesPermissions.config"

import type {
    IConfigurationsAvailableTab,
    IMenuItem,
    IOrganizationAvailableTab,
    IProfileAvailableTab,
} from "../types/objects.types"
import { FEATURE_FLAGS_CONFIG } from "./featureFlags.config"
import { GENERAL_CONFIG } from "./general.config"
import { ROUTES_CONFIG } from "./routes.config"

export const MENU_CONFIG = {
    mainMenu: [
        {
            label: "Checker",
            url: ROUTES_CONFIG.coverageCheckerUrl,
            featureFlag: FEATURE_FLAGS_CONFIG.checkerFeatureFlags,
            featureFlagExistenceOperator:
                FeatureFlagsExistenceLogicalOperator.OR,
        },
        {
            label: "Reports",
            url: ROUTES_CONFIG.reportsUrl,
            submenu: [
                {
                    label: "General",
                    url: ROUTES_CONFIG.reportsUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_GENERAL_REPORTS,
                },
                {
                    label: "Medicaid",
                    url: ROUTES_CONFIG.medicaidReportsUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_MEDICAID_REPORTS,
                },
                {
                    label: "Overrides",
                    url: ROUTES_CONFIG.overridesUrl,
                    neededPermissions: [
                        OneVerifyPermission.overrideCoverageData,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_OVERRIDES,
                },
                {
                    label: "Resets",
                    url: ROUTES_CONFIG.planYearResetsUrl,
                    neededPermissions: [OneVerifyPermission.viewPlanYearResets],
                    featureFlag: FeatureFlag.ONE_VERIFY_PLAN_YEAR_RESETS,
                },
                {
                    label: "Agentic",
                    url: ROUTES_CONFIG.agenticReportsUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_AGENTIC_REPORTS,
                },
                {
                    label: "Search",
                    url: ROUTES_CONFIG.searchUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_SEARCH,
                },
            ],
        },

        {
            label: "Workflows",
            url: ROUTES_CONFIG.workflowManagementUrl,
            neededPermissions: [
                OneVerifyPermission.manageTaskAssignments,
                OneVerifyPermission.workTaskAssignments,
                OneVerifyPermission.manageBillerGroups,
            ],
            featureFlag: [
                FeatureFlag.ONE_VERIFY_TASKS,
                FeatureFlag.ONE_VERIFY_BILLER_GROUPS,
            ],
            submenu: [
                {
                    label: "Policy Assignment",
                    url: ROUTES_CONFIG.taskAssignmentsUrl,
                    neededPermissions: [
                        OneVerifyPermission.manageTaskAssignments,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_TASKS,
                    // Shows if: user has manageTaskAssignments OR (user has workTaskAssignments AND ONE_VERIFY_BILLER_SELF_ASSIGN flag is enabled)
                    combinedPermissions: [
                        {
                            neededPermissions: [
                                OneVerifyPermission.workTaskAssignments,
                            ],
                            featureFlag:
                                FeatureFlag.ONE_VERIFY_BILLER_SELF_ASSIGN,
                        },
                    ],
                },
                {
                    label: "Assigned Policies",
                    url: ROUTES_CONFIG.assignedTasksUrl,
                    neededPermissions: [
                        OneVerifyPermission.workTaskAssignments,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_TASKS,
                },
                {
                    label: "Manage Groups",
                    url: ROUTES_CONFIG.manageGroupsUrl,
                    neededPermissions: [OneVerifyPermission.manageBillerGroups],
                    featureFlag: FeatureFlag.ONE_VERIFY_BILLER_GROUPS,
                },
            ],
        },

        {
            label: "Insights",
            url: ROUTES_CONFIG.insightsUrl,
            neededPermissions: [OneVerifyPermission.viewPracticeInsights],
        },

        {
            label: "Integrations",
            url: ROUTES_CONFIG.integrationsUrl,
            neededPermissions: [OneVerifyPermission.managePracticeIntegrations],
            featureFlag: FeatureFlag.ONE_VERIFY_INTEGRATIONS,
        },
    ] as IMenuItem[],

    accountMenu: [
        {
            label: "Account",
            url: ROUTES_CONFIG.profileUrl,
        },
        {
            label: "Organization",
            url: ROUTES_CONFIG.organizationUrl,
        },
        {
            label: "Configurations",
            url: ROUTES_CONFIG.configurationsUrl,
            neededPermissions: [
                OneVerifyPermission.readPracticeConfigurations,
                OneVerifyPermission.managePracticeConfigurations,
            ],
            // Note: when you remove early release feature flag, you need to remove the payer rates tab from the organization page
            featureFlag: FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
        },
        {
            label: "Network Status",
            url: ROUTES_CONFIG.networkStatusRulesUrl,
            neededPermissions: [OneVerifyPermission.manageNetworkStatusRules],
            featureFlag: FeatureFlag.ONE_VERIFY_NETWORK_STATUS_RULES,
        },
        // Payers monitor watches production requests, so there's nothing to show in sandbox
        ...(!GENERAL_CONFIG.isSandbox
            ? [
                  {
                      label: "Payer Status",
                      url: ROUTES_CONFIG.payerStatusUrl,
                  },
              ]
            : []),
        {
            label: "Helpdesk & Support",
            url: GENERAL_CONFIG.supportPortalUrl,
            isNewTab: true,
        },
        {
            label: "Log out",
            url: ROUTES_CONFIG.logoutUrl,
        },
    ] as IMenuItem[],

    profileAvailableTabs: [
        {
            key: "account",
            label: "Account Details",
        },
    ] as IProfileAvailableTab[],

    organizationAvailableTabs: [
        {
            key: "information",
            label: "Organization",
        },
        {
            key: "users",
            label: "Users",
            neededPermissions: [OneVerifyPermission.managePracticeUsers],
        },
        // TODO: Remove the payer rates tab once we remove the early release feature flag for configurations page
        {
            key: "payer_rates",
            label: "Payer Rates",
            neededPermissions: [
                OneVerifyPermission.readPracticeConfigurations,
                OneVerifyPermission.managePracticeConfigurations,
            ],
            neededPermissionsLogicalOperator:
                PermissionsExistenceLogicalOperator.OR,
        },
    ] as IOrganizationAvailableTab[],

    configurationsAvailableTabs: [
        {
            key: "payer_mappings",
            label: "Payer Mappings",
            neededPermissions: [
                OneVerifyPermission.readPracticeConfigurations,
                OneVerifyPermission.managePracticeConfigurations,
            ],
        },
        {
            key: "payer_rates",
            label: "Payer Rates",
            neededPermissions: [
                OneVerifyPermission.readPracticeConfigurations,
                OneVerifyPermission.managePracticeConfigurations,
            ],
            neededPermissionsLogicalOperator:
                PermissionsExistenceLogicalOperator.OR,
        },
    ] as IConfigurationsAvailableTab[],
}
